import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./LegalInfo.css";

/** Map of section keys → nice labels */
const SECTIONS = [
  { key: "terms",       label: "Terms of Service" },
  { key: "disclaimer",  label: "Service Disclaimer" },
  { key: "liability",   label: "Liability Limitations" },
  { key: "intellectual",label: "Intellectual Property" },
  { key: "compliance",  label: "Compliance Framework" },
];

const LegalInfo = () => {
  const location = useLocation();
  const initialFromHash = useMemo(() => {
    const h = (location.hash || "").replace("#", "");
    return SECTIONS.some(s => s.key === h) ? h : "terms";
  }, [location.hash]);

  const [activeSection, setActiveSection] = useState(initialFromHash);

  // Keep active section in the URL (so you can deep link / refresh)
  useEffect(() => {
    if (!activeSection) return;
    const newHash = `#${activeSection}`;
    if (location.hash !== newHash) {
      window.history.replaceState(null, "", newHash);
    }
    // Make sure the content scolls to top of card when switching
    const target = document.getElementById("legal-content-top");
    if (target) target.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [activeSection, location.hash]);

  const onSelect = (key) => setActiveSection(key);

  return (
    <div className="row clearfix">
      <div className="col-sm-2" />
      <div className="col-sm-9" style={{ padding: 0 }}>
        <div className="container-fluid">

          {/* Breadcrumb */}
          <div className="block-header">
            <div className="row clearfix">
              <div className="col-md-6 col-sm-12">
                <h2>Legal Information</h2>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Legal Info
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>

          <div className="row clearfix">
            {/* LEFT: sidebar */}
            {/* LEFT: Legal Documents menu */}
            <div className="col-xl-3 col-lg-3 col-md-12">
              <div className="card legal-menu-card">
                <div className="header">
                  <h2>Legal Documents</h2>
                </div>
                <div className="body">
                  <ul className="legal-menu">
                    {[
                      { key: "terms", label: "Terms of Service" },
                      { key: "disclaimer", label: "Service Disclaimer" },
                      { key: "liability", label: "Liability Limitations" },
                      { key: "intellectual", label: "Intellectual Property" },
                      { key: "compliance", label: "Compliance Framework" },
                    ].map((item) => (
                      <li key={item.key}>
                        <button
                          type="button"
                          className={
                            "legal-menu-item" +
                            (activeSection === item.key ? " is-active" : "")
                          }
                          onClick={() => setActiveSection(item.key)}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>


            {/* RIGHT: content */}
            <div className="col-xl-9 col-lg-9 col-md-12">
              <div className="card">
                <div className="body">
                  <div id="legal-content-top" />

                  {activeSection === "terms" && (
                    <section aria-labelledby="h-terms">
                      <div className="content-head">
                        <h3 id="h-terms">Terms of Service</h3>
                        <p className="text-muted">These terms govern your enterprise use of the iOdin platform and services.</p>
                      </div>
                      <hr />
                      <h5>1. Service Agreement</h5>
                      <p>
                        iOdin provides external threat surface management and cybersecurity advisory services.
                        By using our platform, your organization agrees to these terms in addition to any signed
                        Order Forms, Statements of Work, or Master Service Agreement.
                      </p>

                      <h5>2. Service Scope</h5>
                      <ul>
                        <li>External attack surface discovery and monitoring</li>
                        <li>Digital presence protection and brand monitoring</li>
                        <li>VAPT (Vulnerability Assessment and Penetration Testing) services</li>
                        <li>Expert cybersecurity advisory and consultation</li>
                        <li>Threat intelligence and incident response support</li>
                      </ul>

                      <h5>3. Client Responsibilities</h5>
                      <p>
                        Provide accurate domain and asset information and ensure you have authorization
                        for any scoped testing. Implementation of recommendations remains your responsibility.
                      </p>

                      <h5>4. Service Limitations</h5>
                      <p>
                        Unless explicitly contracted, services focus on external threat monitoring and do not
                        include internal network security, EDR, or endpoint hardening.
                      </p>

                      <h5>5. Data Handling</h5>
                      <p>
                        We analyze publicly available data related to your digital assets and handle all findings
                        as confidential, sharing them only with authorized stakeholders under your account.
                      </p>

                      <div className="btn-row mt-3">
                        <a href="#" onClick={(e)=>e.preventDefault()} className="btn btn-primary btn-round">Download Master Terms (PDF)</a>
                        <a href="#" onClick={(e)=>e.preventDefault()} className="btn btn-outline-primary btn-round">View Changelog</a>
                      </div>
                    </section>
                  )}

                  {activeSection === "disclaimer" && (
                    <section aria-labelledby="h-disclaimer">
                      <div className="content-head">
                        <h3 id="h-disclaimer">Service Disclaimer</h3>
                        <p className="text-muted">Professional cybersecurity service disclaimers.</p>
                      </div>
                      <hr />
                      <h5>Security Assessment Disclaimer</h5>
                      <p>
                        Assessments are based on authorized scope and publicly accessible information at the time of testing.
                        New vulnerabilities can appear after completion.
                      </p>

                      <h5>Threat Intelligence</h5>
                      <p>
                        Intelligence is aggregated from multiple sources. Completeness or continuous availability of all
                        sources cannot be guaranteed.
                      </p>

                      <h5>Advisory & Recommendations</h5>
                      <p>
                        Recommendations reflect current best practices and the known threat landscape. Final implementation
                        decisions and change control remain with the client.
                      </p>

                      <h5>VAPT Scope</h5>
                      <p>
                        Penetration testing is limited to the agreed scope. Findings outside the scope may not be detected.
                      </p>
                    </section>
                  )}

                  {activeSection === "liability" && (
                    <section aria-labelledby="h-liability">
                      <div className="content-head">
                        <h3 id="h-liability">Liability Limitations</h3>
                        <p className="text-muted">Understand what is and isn’t covered.</p>
                      </div>
                      <hr />
                      <h5>Service Limitations</h5>
                      <p>
                        iOdin provides monitoring and advisory services. We are not liable for incidents that occur despite
                        reasonable safeguards and recommendations.
                      </p>

                      <h5>Maximum Liability</h5>
                      <p>
                        Unless otherwise agreed in writing, liability is capped at fees paid in the preceding twelve (12) months.
                      </p>

                      <h5>Excluded Damages</h5>
                      <p>
                        No indirect, consequential, special, or punitive damages (including data loss or business interruption).
                      </p>

                      <h5>Client Responsibility</h5>
                      <p>
                        Clients are responsible for implementing and maintaining security controls within their environment.
                      </p>
                    </section>
                  )}

                  {activeSection === "intellectual" && (
                    <section aria-labelledby="h-ip">
                      <div className="content-head">
                        <h3 id="h-ip">Intellectual Property</h3>
                        <p className="text-muted">Copyrights, trademarks, and usage rights.</p>
                      </div>
                      <hr />
                      <h5>iOdin Platform</h5>
                      <p>
                        The platform, software, reports, and methodologies are proprietary to iOdin. No rights are granted
                        except as expressly set forth in your agreement.
                      </p>

                      <h5>Client Data</h5>
                      <p>
                        You retain all rights to your data and assets. iOdin accesses publicly available data for monitoring
                        and processes data under your instructions for contracted services.
                      </p>

                      <h5>Reports and Findings</h5>
                      <p>
                        Reports are confidential and intended solely for your organization’s internal use.
                      </p>

                      <h5>Trademarks</h5>
                      <p>
                        iOdin, Valkyrie Services, and related marks are trademarks of iOdin Cybersecurity.
                        Third-party marks belong to their respective owners.
                      </p>
                    </section>
                  )}

                  {activeSection === "compliance" && (
                    <section aria-labelledby="h-compliance">
                      <div className="content-head">
                        <h3 id="h-compliance">Compliance Framework</h3>
                        <p className="text-muted">Standards we align with and how we support audits.</p>
                      </div>
                      <hr />
                      <h5>Industry Standards</h5>
                      <ul>
                        <li>ISO/IEC 27001 – Information Security Management</li>
                        <li>NIST Cybersecurity Framework</li>
                        <li>SOC 2 Type II – Security & Availability</li>
                        <li>GDPR – General Data Protection Regulation (processor role)</li>
                      </ul>

                      <h5>Regulatory Support</h5>
                      <p>
                        Our services help you evidence controls for PCI DSS, HIPAA, SOX and other frameworks.
                      </p>

                      <h5>Audit Support</h5>
                      <p>
                        We provide documentation, data export, and evidence to support your audits and assessments.
                      </p>

                      <h5>Data Protection</h5>
                      <p>
                        Data handling follows applicable privacy laws. Data subject requests can be actioned via
                        customer instructions.
                      </p>

                      <div className="btn-row mt-3">
                        <Link className="btn btn-outline-primary btn-round" to="/privacy">Read Privacy Notice</Link>
                        <a href="#" onClick={(e)=>e.preventDefault()} className="btn btn-outline-primary btn-round">Download DPA (PDF)</a>
                      </div>
                    </section>
                  )}

                  {!activeSection && (
                    <div className="text-center py-5">
                      <h4>Select a legal document from the left</h4>
                      <p className="text-muted">Choose a category to view detailed information.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Small footer note */}
              <div className="card">
                <div className="body">
                  <small className="text-muted">
                    This page is for information only and isn’t legal advice. If there’s a conflict,
                    your signed agreement controls.
                  </small>
                </div>
              </div>
            </div>
            {/* /RIGHT */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalInfo;